'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { Toast, type ToastVariant } from './Toast'

interface ToastInput {
  variant?: ToastVariant
  title: string
  description?: string
  duration?: number
}

interface ToastRecord extends ToastInput {
  id: number
  show: boolean
}

interface ToastApi {
  notify: (input: ToastInput) => void
  success: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
  warning: (title: string, description?: string) => void
  danger: (title: string, description?: string) => void
}

const ToastContext = React.createContext<ToastApi | null>(null)

const DEFAULT_DURATION = 5000

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([])
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  const dismiss = React.useCallback((id: number) => {
    // Hide first (drives the leave transition); then remove from state shortly after.
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, show: false } : t)))
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 200)
  }, [])

  const notify = React.useCallback(
    (input: ToastInput) => {
      const id = Date.now() + Math.random()
      setToasts((prev) => [...prev, { ...input, id, show: true }])
      const duration = input.duration ?? DEFAULT_DURATION
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration)
      }
    },
    [dismiss],
  )

  const api = React.useMemo<ToastApi>(
    () => ({
      notify,
      success: (title, description) => notify({ variant: 'success', title, description }),
      info: (title, description) => notify({ variant: 'info', title, description }),
      warning: (title, description) => notify({ variant: 'warning', title, description }),
      danger: (title, description) => notify({ variant: 'danger', title, description }),
    }),
    [notify],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {mounted &&
        createPortal(
          <div
            aria-live="polite"
            aria-atomic="true"
            className="pointer-events-none fixed inset-0 z-[100] flex flex-col items-end justify-end gap-2 p-4 sm:p-6"
          >
            {toasts.map((t) => (
              <Toast
                key={t.id}
                show={t.show}
                variant={t.variant}
                title={t.title}
                description={t.description}
                onClose={() => dismiss(t.id)}
              />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return ctx
}
